import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { CryptocoinService } from "@main-services/currency/cryptocoin.service";
import { ContactComponent } from "../contact.interface";
import { Contact, ContactField, Cryptocoin } from "@models";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { equal } from "@helpers/utils";
import { ContactService } from "@main-services/contact.service";
import { UserService } from "@main-services/user.service";
import { InfoService } from "@core-services/info.service";
import { SendEntryPointService } from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { TranslationService } from "@core-services/translation.service";
import { WalletsBalanceStatus } from "@modules/+new/core/wallets-balances/domain/entities/wallets-balance-status.model";
import { ContactEditComponent } from "@modules/main/components/contact/contact-edit-modal/contact-edit.component";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { take } from "rxjs/operators";
import { MultichainContactFormComponent } from "@modules/main/components/multichain-contact/multichain-contact-form/multichain-contact-form.component";

@Component({
	selector: "app-contact-view",
	templateUrl: "./contact-view.component.html",
	styleUrls: ["./contact-view.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ContactViewComponent extends ContactComponent implements OnInit {
	@Input()
	public data: Contact = null;
	public contactFields: ContactField[] = [];
	public monogram: string = null;
	public contactName: string = null;

	public constructor(
		protected readonly contactService: ContactService,
		protected readonly infoService: InfoService,
		protected readonly userService: UserService,
		protected readonly coinService: CryptocoinService,
		private readonly walletService: TradableWalletListService,
		protected readonly sendEntryPoint: SendEntryPointService,
		protected readonly translationService: TranslationService,
		protected readonly launchDarkly: LaunchDarklyService
	) {
		super(contactService, infoService, userService, coinService, sendEntryPoint, translationService);
	}

	public ngOnInit(): void {
		this.setContactFields();
		this.monogram = this.data.getMonogram();
		this.contactName = this.data.getName();
	}

	public edit(): void {
		this.launchDarkly
			.isActive$(LD_FLAG.WITHDRAWAL_MULTICHAIN)
			.pipe(take(1))
			.subscribe((isActive) => {
				if (!isActive) {
					this.modalService.showModal(ContactEditComponent, this.data);
				} else {
					// open new contact form -> from existing contact
					this.modalService
						.create(MultichainContactFormComponent, {
							newContact: false,
							contact: this.data
						})
						.present();
				}
			});
	}

	private setContactFields(): void {
		this.contactFields = this.data.getFields(false, true);
		this.contactFields.sort((a, b) => (a.getType() !== "email" && b.getType() === "email" ? 1 : -1)); // Sorting so email is always first
	}

	public async send(c: Contact, selectedAddress: ContactField = null): Promise<void> {
		const coin: Cryptocoin = this.coinService.findBySymbol(selectedAddress.getSymbol());
		const status: WalletsBalanceStatus = await this.walletService.getCoinWalletsBalances(coin.getId());
		if (!equal(selectedAddress.getSymbol(), "EMAIL")) {
			if (status.balances.every((balance) => balance.operationalBalance <= 0) && status.hasUnconfirmedBalance) {
				this.infoService.setError(this.translationService.translate("recent_deposit_disclaimer"));
				return;
			}

			if (
				this.walletService.getNonEmptyWallets(coin).length === 0 ||
				(status.balances.every((balance) => balance.operationalBalance <= 0) && !status.hasUnconfirmedBalance)
			) {
				this.infoService.setError(this.translationService.translate("cannot_send_because_no_funds_contacts"));
				return;
			}

			if (!coin.isWithdrawActive() || coin.isMaintenance()) {
				this.infoService.setError(this.translationService.translate("action_send_inactive_tooltip"));
				return;
			}
		}

		await super.send(c, selectedAddress, status.balances[0].walletGuid);
	}
}
